// LIAISON-Theme über FluentProvider und semantische Tokens (ADR-0002, ADR-0033).
//
// Es entsteht KEINE zweite Styleebene. ADR-0002 verbietet die Mischung von
// Fluent- und Fremdprimitives ausdrücklich, und ein eigenes CSS-Framework
// daneben wäre genau das. Was hier steht, ist eine Markenrampe für Fluents
// eigenes Themesystem -- nichts weiter.
//
// SEIT ADR-0033 IST ES DIE ECHTE MARKE. Vorher trug die Anwendung Fluents
// Web-Blau `#0f6cbd`, obwohl `docs/brand/README.md` seit jeher `#2164AE` als
// Markenblau führt. Das war keine Entscheidung, sondern eine Lücke: das
// Markenhandbuch ist ein freigegebenes Artefakt mit Owner und Gate, und die
// Anwendung verwendete davon nichts.

import { createDarkTheme, createLightTheme, type BrandVariants, type Theme } from '@fluentui/react-components';

/**
 * Die Markenrampe, aus den Ankerpunkten von `docs/brand/README.md` erzeugt.
 *
 * WIE SIE ENTSTANDEN IST, weil eine Rampe aus sechzehn Farbwerten sonst wie
 * geraten aussieht: Fluents eigene Web-Rampe ist kontrastgeprüft, und ihre
 * HELLIGKEITSABSTÄNDE tragen genau diese Prüfung. Übernommen wurde deshalb ihre
 * Helligkeitsstruktur; Farbton und Sättigung kommen aus den vier OKLCH-Ankern
 * des Markenhandbuchs -- Markenblau, Akzentblau, Dark-Mode-Blau und Hellblau --
 * und werden dazwischen interpoliert.
 *
 * Die ganze Rampe ist anschließend um `-0.026` in der Helligkeit verschoben,
 * damit Stufe 80 EXAKT auf `#2164AE` fällt. Das ist kein Schönheitswert: Fluents
 * `createLightTheme` nimmt Stufe 80 als `colorBrandBackground`, also als Fläche
 * jeder Hauptaktion. Ohne die Verschiebung läge dort `#256cb7` -- nah am
 * Markenblau, aber eben nicht das Markenblau, und niemand würde den Unterschied
 * je nachrechnen.
 *
 * Gemessen: Weiß auf Stufe 80 erreicht 6,00:1. WCAG 1.4.3 verlangt 4,5:1 für
 * normalen Text. Die Rampe erfüllt damit die Anforderung, an der die vorherige
 * Fassung mit 3,8:1 gescheitert war -- und ist dabei markenkonform statt nur
 * korrekt.
 */
const liaisonBrand: BrandVariants = {
  10: '#021023',
  20: '#041b36',
  30: '#072647',
  40: '#0c325b',
  50: '#113e6f',
  60: '#164b84',
  70: '#1b5696',
  80: '#2164ae',
  90: '#2e7ece',
  100: '#4197e4',
  110: '#56a5e7',
  120: '#6bb0ea',
  130: '#88c0ef',
  140: '#a4cff4',
  150: '#beddf8',
  160: '#daecfb',
};

/**
 * Die Oberflächenschrift aus `docs/brand/README.md`, mit derselben Folge wie
 * `body` in `index.html`.
 *
 * Sie muss im Thema stehen und nicht nur dort: FluentProvider setzt
 * `fontFamilyBase` auf seinen eigenen Wurzelknoten und überschreibt damit
 * `body` für alles, was er umschließt. Fluents Vorgabe beginnt mit Segoe UI;
 * unter macOS und Linux erschien die Anwendung deshalb in Helvetica oder
 * Roboto, obwohl Inter Tight geladen war. Die Folge danach greift nur, solange
 * die Schrift noch lädt.
 */
const LIAISON_SCHRIFT =
  "'Inter Tight', 'Segoe UI', -apple-system, BlinkMacSystemFont, system-ui, 'Helvetica Neue', Arial, sans-serif";

/**
 * Die v4-Werte, die das Theme setzt (Entwurf v4, `:root` hell und dunkel).
 * Kontrast berechnet und im Browser gemessen: test/theme.test.ts, Probe B62.
 *
 * Bewusst NICHT gesetzt:
 *   * `colorStrokeFocus2`: Fluents Doppelrahmen haelt auf jeder Flaeche 3:1,
 *     ein blauer Rahmen verschwaende auf Markenblau.
 *   * `colorNeutralStroke1` ist die Zierlinie `line-strong` und NIE die
 *     alleinige Grenze eines Bedienelements; die Feldgrenze traegt
 *     `colorNeutralStrokeAccessible`. Hell gilt v4 (1,62:1, Fluent: 1,53:1).
 *     Dunkel bleibt Fluents #666666 (2,88:1 auf der v4-Flaeche): v4 #46505d
 *     haette die Raender der Knoepfe mit 2,02:1 unter Fluents eigenen Stand
 *     gedrueckt (Codex-Cloud-Review #399). Sekundaere Knoepfe tragen ihren Namen
 *     als Text; die Grenze allein identifiziert sie nicht.
 *   * `overdue` faellt mit `bad-fg` zusammen, `fs-s` bleibt 12 px.
 *   * Die inaktiv-Pille (`off-*`) liest Fluents tint-informative
 *     (Foreground3 auf Background4); gemessen sind das ink-3 auf sunken.
 */
const V4_HELL: Partial<Theme> = {
  colorNeutralBackground1: '#ffffff',
  colorNeutralBackground2: '#f8fafc',
  colorNeutralBackground3: '#f4f6f9',
  colorNeutralBackground4: '#eef2f6',
  colorNeutralForeground1: '#16191f',
  colorNeutralForeground2: '#47505e',
  colorNeutralForeground3: '#5e6878',
  colorNeutralStroke2: '#dde3ea',
  colorNeutralStroke1: '#c3ccd7',
  colorBrandBackground2: '#e6f0fa',
  colorBrandForegroundLink: '#1b5696',
  colorBrandForegroundLinkHover: '#1b5696',
  colorPaletteGreenForeground1: '#1d6b3a',
  colorPaletteGreenBackground1: '#e2f3e7',
  colorStatusSuccessForeground1: '#1d6b3a',
  colorStatusSuccessBackground1: '#e2f3e7',
  colorPaletteYellowForeground1: '#7a4a00',
  colorPaletteYellowBackground1: '#fff0d4',
  colorStatusWarningForeground1: '#7a4a00',
  colorStatusWarningBackground1: '#fff0d4',
  colorPaletteRedForeground1: '#a3261f',
  colorPaletteRedBackground1: '#fde9e7',
  colorStatusDangerForeground1: '#a3261f',
  colorStatusDangerBackground1: '#fde9e7',
  borderRadiusMedium: '6px',
  shadow4: '0 1px 2px rgba(22,25,31,.06), 0 2px 8px rgba(22,25,31,.05)',
};

const V4_DUNKEL: Partial<Theme> = {
  colorNeutralBackground1: '#1b1f26',
  colorNeutralBackground2: '#20252d',
  // Markentinte aus docs/brand/README.md (dieselbe Farbe traegt die Wortmarke),
  // nicht v4 `paper` #121519: das Handbuch geht vor dem Entwurf.
  colorNeutralBackground3: '#16191f',
  colorNeutralBackground4: '#262c35',
  colorNeutralForeground1: '#eceff4',
  colorNeutralForeground2: '#c3cad4',
  colorNeutralForeground3: '#a4aebb',
  colorNeutralStroke2: '#2f3640',
  colorBrandBackground2: '#1c2f44',
  colorBrandForegroundLink: '#84bfee',
  colorBrandForegroundLinkHover: '#84bfee',
  // Das Dark-Mode-Blau des Handbuchs (= v4 `brand`) tragen Primaerknoepfe; der Kopf
  // der Anwendung hat seit G2a eigene Farben (shell/masse.ts) und liest diese
  // Token nicht mehr.
  colorBrandBackground: '#62ace8',
  colorBrandBackgroundHover: '#84bfee',
  colorBrandBackgroundPressed: '#4197e4',
  colorNeutralForegroundOnBrand: '#10141a',
  colorPaletteGreenForeground1: '#8fdaa8',
  colorPaletteGreenBackground1: '#16301f',
  colorStatusSuccessForeground1: '#8fdaa8',
  colorStatusSuccessBackground1: '#16301f',
  colorPaletteYellowForeground1: '#f3c46a',
  colorPaletteYellowBackground1: '#36290f',
  colorStatusWarningForeground1: '#f3c46a',
  colorStatusWarningBackground1: '#36290f',
  colorPaletteRedForeground1: '#ffb4ab',
  colorPaletteRedBackground1: '#3b1a17',
  colorStatusDangerForeground1: '#ffb4ab',
  colorStatusDangerBackground1: '#3b1a17',
  borderRadiusMedium: '6px',
  shadow4: '0 1px 2px rgba(0,0,0,.3)',
};

export const liaisonLightTheme: Theme = {
  ...createLightTheme(liaisonBrand),
  fontFamilyBase: LIAISON_SCHRIFT,
  // Papier als unterste Flaeche: ohne eine Flaeche UNTER der Karte schwebt die
  // Karte auf demselben Weiss (docs/brand/README.md nennt den Wert Papier).
  ...V4_HELL,
};

/**
 * Dunkles Thema, aus DERSELBEN Rampe abgeleitet (ST-UX-001, NFR-ACC-001).
 *
 * Es gehoert dazu und ist kein Komfortmerkmal: die Systemeinstellung "dunkel"
 * wird unter anderem wegen Lichtempfindlichkeit, Migraene und
 * Sehbeeintraechtigung gesetzt. Die Flaechen-, Text- und Statuswerte kommen aus
 * v4 und sind nachgerechnet (V4_DUNKEL); die Markenstufen bleiben Fluents
 * Ableitung aus der Rampe.
 */
export const liaisonDarkTheme: Theme = {
  ...createDarkTheme(liaisonBrand),
  fontFamilyBase: LIAISON_SCHRIFT,
  ...V4_DUNKEL,
};
